import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { fetchUpcomingBirthdays } from '../api/client';
import type { UpcomingBirthday } from '../api/types';
import { useLocale } from '../i18n/LocaleContext';
import { dismissBirthday, readDismissedBirthdays } from '../screens/birthdayDismissStorage';
import type { ChatListStyles } from '../theme/chatListStylesFromPalette';
import { birthdaysSoon, birthdaysToday, daysUntilBirthday, localDateKey } from '../util/birthdays';
import { hapticLight } from '../util/haptics';
import { BirthdayGreetingModal } from './BirthdayGreetingModal';

type Props = {
  /** Стили списка чатов: панель живёт внутри него и обязана выглядеть его частью. */
  styles: ChatListStyles;
  /** Панель молчит, пока сессии нет: без токена запрос всё равно вернёт отказ. */
  enabled: boolean;
};

/** Насколько вперёд смотрит строка «скоро». Неделя — то, к чему ещё можно подготовиться. */
const SOON_WINDOW_DAYS = 7;

/**
 * Напоминание о днях рождения контактов (заказ владельца 2026-08-29).
 *
 * <p>Живёт в списке чатов, а не в «Избранном»: список чатов — первый экран, и напоминание
 * «сегодня день рождения» ценно ровно в тот день, когда его увидят сразу. В «Избранном» оно
 * дождалось бы человека к вечеру.
 *
 * <p>Сегодняшние и ближайшие разведены. У сегодняшних есть действие («Поздравить»), у
 * ближайших — нет: поздравлять заранее не принято, и кнопка, которая это предлагает, толкает
 * сделать неловкость.
 *
 * <p>Скрытие — локальное (`birthdayDismissStorage`): поздравить можно и голосом, и в другом
 * мессенджере, и карточка после этого обязана уйти, хотя сервер об этом ничего не знает.
 */
export function BirthdayRemindersPanel({ styles, enabled }: Props): React.ReactElement | null {
  const { t } = useLocale();

  const [rows, setRows] = useState<UpcomingBirthday[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [greeting, setGreeting] = useState<UpcomingBirthday | null>(null);
  /** Строка «Поздравление отправлено»; сама гаснет — подтверждение не должно висеть до утра. */
  const [notice, setNotice] = useState<string | null>(null);

  const year = new Date().getFullYear();

  // Выключили напоминания — список пустеет в том же рендере, без запроса.
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (prevEnabled !== enabled) {
    setPrevEnabled(enabled);
    if (!enabled) setRows([]);
  }

  useEffect(() => {
    if (!enabled) return;
    // Дату шлём свою: «сегодня» у сервера другое, и посчитанное по его календарю «через
    // 3 дня» ошибается на сутки у всех, кто живёт восточнее или западнее.
    Promise.all([fetchUpcomingBirthdays(localDateKey()), readDismissedBirthdays(year)]).then(
      ([list, hidden]) => {
        setRows(list);
        setDismissed(hidden);
      },
      () => {
        // Молча: дни рождения — не то, ради чего показывают ошибку поверх списка чатов.
        setRows([]);
      },
    );
  }, [enabled, year]);

  /** Подтверждение само гаснет: висящее до утра «Поздравление отправлено» — это уже мусор. */
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  const today = useMemo(
    () => birthdaysToday(rows).filter((r) => !dismissed.has(r.userId)),
    [rows, dismissed],
  );
  const soon = useMemo(() => birthdaysSoon(rows, SOON_WINDOW_DAYS), [rows]);

  // Подтверждение показывается и после того, как поздравленный исчез из списка: иначе
  // единственным следом отправки был бы пропавший ряд, а это неотличимо от «нажал мимо».
  if (today.length === 0 && soon.length === 0 && !notice) {
    return null;
  }

  async function hide(userId: string) {
    hapticLight();
    await dismissBirthday(userId, year);
    setDismissed((prev) => new Set(prev).add(userId));
  }

  function whenLabel(row: UpcomingBirthday): string {
    const days = daysUntilBirthday(row.birthMonth, row.birthDay);
    if (days === 1) return t('birthdayTomorrow');
    return t('birthdayInDays').replace('{days}', String(days));
  }

  return (
    <>
      <View style={styles.druzhinaPanel}>
        {notice ? <Text style={styles.settingsSavedNote}>{notice}</Text> : null}
        {today.length > 0 ? (
          <>
            <Text style={styles.druzhinaTitle}>{`🎂 ${t('birthdayTodayTitle')}`}</Text>
            {today.map((row) => (
              <View key={row.userId} style={styles.druzhinaRow}>
                <Text style={styles.druzhinaName} numberOfLines={1}>
                  {typeof row.turningAge === 'number'
                    ? `${row.displayName} — ${t('birthdayTurns').replace('{age}', String(row.turningAge))}`
                    : row.displayName}
                </Text>
                <Pressable
                  onPress={() => {
                    hapticLight();
                    setGreeting(row);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`${t('birthdayCongratulate')}: ${row.displayName}`}>
                  <Text style={styles.druzhinaAdd}>{t('birthdayCongratulate')}</Text>
                </Pressable>
                <Pressable
                  onPress={() => void hide(row.userId)}
                  accessibilityRole="button"
                  accessibilityLabel={`${t('birthdayHide')}: ${row.displayName}`}>
                  <Text style={styles.druzhinaHint}>{t('birthdayHide')}</Text>
                </Pressable>
              </View>
            ))}
          </>
        ) : null}
        {soon.length > 0 ? (
          <>
            <Text style={styles.druzhinaHint}>{t('birthdaySoonTitle')}</Text>
            {soon.map((row) => (
              <Text key={row.userId} style={styles.druzhinaHint} numberOfLines={1}>
                {`${row.displayName} · ${whenLabel(row)}`}
              </Text>
            ))}
          </>
        ) : null}
      </View>
      <BirthdayGreetingModal
        target={greeting}
        onClose={() => setGreeting(null)}
        // Поздравил — карточка уходит так же, как по «Скрыть»: возвращать её после отправки
        // значит предлагать поздравить второй раз.
        onSent={(userId, confirmation) => {
          setNotice(confirmation);
          void hide(userId);
        }}
      />
    </>
  );
}
